/**
 * -----------------------------------------------------------------------------
 * Popup - Create New Enterprise
 * -----------------------------------------------------------------------------
 * This popup is used for creating a new enterprise.
 */

import { useFormik } from "formik";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { serializeErrorKeyValues, serverErrorMessage } from "../../../api/network/errorCodes";
import { useCreateEnterpriseMutation } from "../../../api/network/sysadminApiServices";
import { setModalsData, TListingQueryParams, TModalsState } from "../../../api/store/commonSlice";
import FormActionButtons from "../../../components/admin/formActionButtons";
import { AdminFormFieldDropdown, AdminFormFieldInput } from "../../../components/admin/formFields";
import FTModal from "../../../components/common/FTModal";
import { TIMEZONE_OPTIONS } from "../../../constants/constants";
import { routeUrls } from "../../../navigation/routeUrls";
import { C_TO_TEMPERATURE_UNITS_VALUES, DISTANCE_UNIT_VALUES, ECONOMY_UNITS_VALUES, PRESSURE_UNITS_VALUES, SPEED_UNIT_VALUES, VOLUME_UNIT_VALUES } from "../../admin-details/account/constants";
import { formInitialValues, TFormFieldNames, YupValidationSchema } from "./validation";

const SysadminEnterprisesCreateNew = () => {
  const { t: tMain } = useTranslation();
  const { t } = useTranslation('translation', { keyPrefix: 'sysadmin.enterprises.create_new' });
  const modalsState = useSelector((state: { commonReducer: { modals: TModalsState } }) => state.commonReducer.modals);
  const listingQueryParams: TListingQueryParams = useSelector((state: any) => state.commonReducer.listingQueryParams);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // enterprise mutation
  const [createEnterpriseAPITrigger] = useCreateEnterpriseMutation();

  const hideModal = () => {
    dispatch(setModalsData({ ...modalsState, showSysadminEnterprisesCreateNew: false }));
  };
      const formik = useFormik({
        initialValues:formInitialValues,
        validationSchema:YupValidationSchema,
        onSubmit:(values, { setSubmitting }) => {
          createEnterpriseAPITrigger({
            accountid: values.account_id,
            contactname: values.contact_name,
            contactphone: values.contact_phone,
            description: values.description,
            timezone: values.timezone,
            contactemail: values.contact_email,
            // extra required fields
            maintlabelkm0: "Last Maintenance #1",
            maintlabelkm1: "Last Maintenance #2",
            maintlabelkm2: "Last Maintenance #3",
            maintlabelkm3: "Last Maintenance #4",
            maintlabelkm4: "Last Maintenance #5",
            maintlabelkm5: "Last Maintenance #6",
            maintlabelkm6: "Last Maintenance #7",
            maintlabelkm7: "Last Maintenance #8",
            maintlabelkm8: "Last Maintenance #9",
            maintlabelkm9: "Last Maintenance #10",
            maintlabelhr0: "Last Eng Hours Maint #1",
            maintlabelhr1: "Last Eng Hours Maint #2",
            maintlabelhr2: "Last Eng Hours Maint #3",
            maintlabelhr3: "Last Eng Hours Maint #4",
            maintlabelhr4: "Last Eng Hours Maint #5",
            allowwebservice: 0,
            enableclustering: 1,
            elogenabled: 0,
            elogaccountid: 0,
            attributemask: 0,
            showspeedingbehavior: 1,
            showdtcpopups: 0,
            allowmaintlabels: 0,
            // pto_labels: false, // TODO: NEED CONFIRMATION
            viewvideoallowed: 0,
            usesso: 0,
            isbordercrossing:0,
            usehamburgermenu: 0,
            hasdrivers: 0,
            usedrivergroups: 0,
            defaultsnappedroute: 0,
            //new added default flags
            isactive: 1,
            privatelabelname:"*",
            allownotify:1,
            geocodermode: "3",
            scorewtharshaccel: 0,
            scorewtharshbrake: 0,
            scorewtreverse: 0,
            scorewtseatbeltoff: 0,
            scorewtspeeding:0,
            scorewtidle: 0,
            scorewtcornering: 0,
            scorewtimpactai: 0,
            scorewtphoneuseai: 0,
            scorewtdistractedai:0,
            scorewtseatbeltoffai:0,
            scorewtfooddrinkai: 0,
            scorewtcorneringai:0,
            scorewtsmokingai: 0,
            scorewtfatigueai:0,
            scorewtobjcameraai: 0,
            scorewttailgatingai:0,
            scorewtfrontcollisionai: 0,
            scorewtpedestriancollisionai: 0,
            scorewtlanedepartureai: 0,
            scorewtstopandgoai: 0,
            scorewtrollingstopai: 0,
            scorewtcamerapowerlossai: 0,
            scorewtcameracoverremovalai: 0,
            scorewt75for30ai: 0,
            updatevinprops: 0,
            usehierarchygroups:0,
            infoboxalwaysopen: 0,
            infoboxassetid: 1,
            infoboxassetdesc: 0,
            infoboxassetdisplay: 0,
            infoboxdriver: 0,
            tomcattimeout:30,
            retainedeventage:63072000,
            maintenance_flag:{
                maint_flag_ft0: true,
                maint_flag_ft1: true,
                maint_flag_ft2: true,
                maint_flag_ft3: true,
                maint_flag_ft4: true,
                maint_flag_hr0: true,
                maint_flag_hr1: true,
                maint_flag_hr2: true,
                maint_flag_hr3: true,
                maint_flag_hr4: true,
                maint_flag_km0: true,
                maint_flag_km1: true,
                maint_flag_km2: true,
                maint_flag_km3: true,
                maint_flag_km4: true,
                maint_flag_km5: true,
                maint_flag_km6: true,
                maint_flag_km8: true,
                maint_flag_km9: true,
            },
            // some default units
            speedunits: parseInt(SPEED_UNIT_VALUES.MPH),
            distanceunits: parseInt(DISTANCE_UNIT_VALUES.MILES),
            volumeunits: parseInt(VOLUME_UNIT_VALUES.GALLONS),
            economyunits: parseInt(ECONOMY_UNITS_VALUES.MPG),
            pressureunits: parseInt(PRESSURE_UNITS_VALUES.KPA),
            temperatureunits: parseInt(C_TO_TEMPERATURE_UNITS_VALUES.FAHRENHEIT),
          })
            .unwrap()
            .then((data) => {
              if (!!data?.accountid) {
                dispatch(setModalsData({ ...modalsState, showSysadminEnterprisesCreateNew: false }));
                toast.success(t('toast.create_success'), { autoClose: 10000 });
                navigate(`${routeUrls.sysadminChildren.enterprises}/${data?.accountid}`, { state: { new: true } });
              } else {
                toast.error(t('toast.create_failed'));
              }
            })
            .catch((error) => {
              // `handleAuthErrorCode` already toasts a body with `error` (duplicate account ID) and
              // network or non-JSON failures. Anything else is a 4xx/5xx the user must still be told about.
              if (error?.data?.error || typeof error?.status !== "number") return;
              toast.error(serverErrorMessage(error) || t('toast.create_failed'));
            })
            .finally(() => {
              setSubmitting(false);
            });
        }
      });
      const{
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        dirty,
        setFieldValue,
        setFieldTouched,
      } = formik ;
        const invalidFields =
          Object.keys(errors)
            .filter(key => !!errors[key as TFormFieldNames]);
        const isFormValid = invalidFields.length === 0 && dirty;
        const MemoizedFormActionButtons = useMemo(() => {
          return (
            <>
              <FormActionButtons
                buttons={[
                  {
                    label:t('submit_button'),
                    variant:"primary",
                    disabled:!isFormValid || isSubmitting /* || isLoading*/
                  },
                  {
                    label:tMain('cancel'),
                    type:"button",
                    variant:"danger-transparent",
                    disabled:isSubmitting , /* || isLoading*/
                    onClick:()=>hideModal()
                  },
                ]}
              />
            </>
          );
        }, [
          isFormValid,
          isSubmitting,    
        ])
        
  return (
    <FTModal
      visible={modalsState.showSysadminEnterprisesCreateNew}
      heading={t("heading")}
      onHide={hideModal}
    >
          <form className="grid grid-cols-6 gap-6" onSubmit={handleSubmit}>
            <p className="col-span-6 mt-0 leading-relaxed text-gray-500">
              {t("sub_heading")}
            </p>

            <AdminFormFieldInput
              label={t('account_id')}
              type="text"
              id="AccountId"
              name="account_id"
              placeholder={t('account_id_placeholder')}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.account_id}
              touched={touched.account_id}
              error={errors.account_id}
            />

            <AdminFormFieldInput
              label={t('description')}
              type="text"
              id="Description"
              name="description"
              placeholder={t('description_placeholder')}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.description}
              touched={touched.description}
              error={errors.description}
            />

            <AdminFormFieldInput
              label={t('contact_name')}
              type="text"
              id="ContactName"
              name="contact_name"
              placeholder={t('contact_name_placeholder')}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.contact_name}
              touched={touched.contact_name}
              error={errors.contact_name}
            />

            <AdminFormFieldInput
              label={t('contact_phone')}
              type="text"
              id="ContactPhone"
              name="contact_phone"
              placeholder={t('contact_phone_placeholder')}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.contact_phone}
              touched={touched.contact_phone}
              error={errors.contact_phone}
            />

            <AdminFormFieldDropdown
              label={t("timezone")}
              id="timezone"
              name="timezone"
              placeholder={t('timezone_placeholder')}
              value={!!values.timezone ? values.timezone : undefined}
              options={
                [
                  ...TIMEZONE_OPTIONS.map((option) => ({
                    value: option.value,
                    label: option.label,
                  }))
                ]
              }
              onChange={(e) => setFieldValue("timezone", e?.value)}
              onBlur={(e) => { setFieldTouched("timezone", true); handleBlur(e); }}
              error={errors.timezone}
              touched={touched.timezone}
            />

            <AdminFormFieldInput
              label={t('user_name')}
              type="text"
              id="UserName"
              name="user_name"
              placeholder={t('user_name_placeholder')}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.user_name}
              touched={touched.user_name}
              error={errors.user_name}
            />

            <AdminFormFieldInput
              label={t('contact_email')}
              type="email"
              id="ContactEmail"
              name="contact_email"
              placeholder={t('contact_email_placeholder')}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.contact_email}
              touched={touched.contact_email}
              error={errors.contact_email}
            />
            {MemoizedFormActionButtons}
            {/* <AdminFormFieldSubmit
              label={t('submit_button')}
              variant="primary"
              disabled={!isFormValid || isSubmitting } // || isLoading
            />

            <AdminFormFieldSubmit
              label={tMain('cancel')}
              type="button"
              variant="danger-transparent"
              disabled={isSubmitting }// || isLoading
              onClick={hideModal}
            /> */}
          </form>
    </FTModal>
  )
};
export default SysadminEnterprisesCreateNew;